import { CalendarEvent, categoryConfig } from '../../../mocks/eventsCalendar';
import { Link } from 'react-router-dom';

interface EventDetailModalProps {
  event: CalendarEvent | null;
  onClose: () => void;
  onRsvp?: () => void;
  isGuest?: boolean;
  onUnlock?: () => void;
}

export default function EventDetailModal({ event, onClose, onRsvp, isGuest = false, onUnlock }: EventDetailModalProps) {
  if (!event) return null;

  const cfg = categoryConfig[event.category];
  const dateObj = new Date(event.date + 'T00:00:00');
  const formattedDate = dateObj.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm"></div>
      <div
        className="relative bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Hero Image */}
        <div className="relative h-56 overflow-hidden rounded-t-2xl">
          <img src={event.image} alt={event.title} className="w-full h-full object-cover object-top" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
          <button
            onClick={onClose}
            className="absolute top-3 right-3 w-8 h-8 flex items-center justify-center bg-white/90 rounded-full cursor-pointer hover:bg-white transition-colors"
          >
            <i className="ri-close-line text-gray-700 text-lg"></i>
          </button>
          {/* Guest badge */}
          {isGuest && (
            <div className="absolute top-3 left-3 flex items-center gap-1.5 bg-black/50 backdrop-blur-sm text-white text-[10px] font-semibold px-2.5 py-1 rounded-full">
              <i className="ri-eye-line text-xs"></i>
              Guest View
            </div>
          )}
          <div className={`absolute bottom-3 left-4 text-xs font-semibold px-3 py-1 rounded-full ${cfg.bg} ${cfg.color}`}>
            <span className={`inline-block w-1.5 h-1.5 rounded-full ${cfg.dot} mr-1`}></span>
            {cfg.label}
          </div>
        </div>

        <div className="p-6">
          {/* Title */}
          <h2 className="text-xl font-bold text-gray-900 mb-4 leading-snug" style={{ fontFamily: "'Playfair Display', serif" }}>
            {event.title}
          </h2>

          {/* Event meta */}
          <div className="space-y-3 mb-5">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 flex items-center justify-center bg-pink-50 rounded-lg shrink-0">
                <i className="ri-calendar-event-line text-pink-500"></i>
              </div>
              <div>
                <div className="text-xs text-gray-400 font-medium">Date</div>
                <div className="text-sm text-gray-800 font-semibold">{formattedDate}</div>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 flex items-center justify-center bg-pink-50 rounded-lg shrink-0">
                <i className="ri-time-line text-pink-500"></i>
              </div>
              <div>
                <div className="text-xs text-gray-400 font-medium">Time</div>
                <div className="text-sm text-gray-800 font-semibold">{event.time}{event.endTime ? ` – ${event.endTime}` : ''}</div>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 flex items-center justify-center bg-pink-50 rounded-lg shrink-0">
                <i className="ri-map-pin-line text-pink-500"></i>
              </div>
              <div>
                <div className="text-xs text-gray-400 font-medium">Location</div>
                <div className="text-sm text-gray-800 font-semibold">{event.location}</div>
                <div className="text-xs text-gray-500">{event.address}</div>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 flex items-center justify-center bg-pink-50 rounded-lg shrink-0">
                <i className="ri-user-heart-line text-pink-500"></i>
              </div>
              <div>
                <div className="text-xs text-gray-400 font-medium">Age Recommendation</div>
                <div className="text-sm text-gray-800 font-semibold">{event.ageRecommendation}</div>
              </div>
            </div>
          </div>

          {/* About */}
          <div className="bg-gray-50 rounded-xl p-4 mb-5">
            <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-2">About This Event</h4>
            <p className="text-sm text-gray-700 leading-relaxed">{event.description}</p>
          </div>

          {/* ── Reserve Your Spot ── */}
          <div className="border border-pink-200 rounded-2xl overflow-hidden mb-4">
            <div className="bg-pink-50 px-4 py-3 flex items-center gap-2 border-b border-pink-100">
              <div className="w-6 h-6 flex items-center justify-center">
                <i className="ri-ticket-2-line text-pink-500 text-base"></i>
              </div>
              <span className="text-sm font-bold text-pink-700 uppercase tracking-wide">Reserve Your Spot</span>
              {isGuest && (
                <div className="ml-auto flex items-center gap-1 text-[10px] font-semibold text-gray-400">
                  <i className="ri-lock-line text-xs"></i>
                  Members only
                </div>
              )}
            </div>

            {/* Pricing rows — blurred for guests */}
            <div className="relative">
              <div className={isGuest ? 'blur-sm select-none pointer-events-none' : ''}>
                <div className="divide-y divide-pink-100">
                  {/* General Admission row */}
                  <div className="flex items-center justify-between px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 flex items-center justify-center bg-white border border-pink-100 rounded-lg shrink-0">
                        <i className="ri-ticket-line text-gray-500 text-sm"></i>
                      </div>
                      <div>
                        <div className="text-sm font-semibold text-gray-800">General Admission</div>
                        <div className="text-xs text-gray-400">Standard entry · limited spots</div>
                      </div>
                    </div>
                    <div className="text-base font-black text-gray-900">$5</div>
                  </div>

                  {/* VIP row */}
                  <div className="flex items-center justify-between px-4 py-3 bg-rose-50/60">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 flex items-center justify-center bg-rose-100 rounded-lg shrink-0">
                        <i className="ri-vip-crown-fill text-rose-500 text-sm"></i>
                      </div>
                      <div>
                        <div className="text-sm font-semibold text-rose-800">VIP — Members Only</div>
                        <div className="text-xs text-rose-500 font-medium">Priority Access · First in line</div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-base font-black text-rose-600">Free</div>
                      <div className="text-[10px] text-rose-400 font-medium leading-none">w/ membership</div>
                    </div>
                  </div>
                </div>

                {/* Fake RSVP button (only visible in blurred state for guests) */}
                <div className="px-4 py-4 bg-white">
                  <div className="w-full py-3 bg-pink-500 rounded-xl flex items-center justify-center gap-2">
                    <i className="ri-checkbox-circle-line text-white text-base"></i>
                    <span className="text-white text-sm font-bold">RSVP NOW</span>
                  </div>
                </div>
              </div>

              {/* Guest lock overlay */}
              {isGuest && (
                <div className="absolute inset-0 flex flex-col items-center justify-center bg-white/80 backdrop-blur-[1px] px-5">
                  <div className="w-10 h-10 flex items-center justify-center bg-gray-100 rounded-full mb-3">
                    <i className="ri-lock-fill text-gray-500 text-lg"></i>
                  </div>
                  <p className="text-sm font-bold text-gray-800 text-center mb-0.5">Pricing &amp; RSVP Locked</p>
                  <p className="text-xs text-gray-500 text-center mb-4">Create a free account to see pricing and secure your spot.</p>
                  <button
                    onClick={onUnlock}
                    className="w-full py-2.5 bg-pink-500 hover:bg-pink-600 text-white text-xs font-bold rounded-xl transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
                  >
                    <i className="ri-lock-unlock-line text-sm"></i>
                    Join Free — Unlock This
                  </button>
                </div>
              )}
            </div>

            {/* RSVP + Directions — only for logged-in users */}
            {!isGuest && (
              <div className="px-4 py-4 bg-white border-t border-pink-100 space-y-2">
                <button
                  onClick={onRsvp}
                  className="w-full py-3 bg-pink-500 hover:bg-pink-600 text-white text-sm font-bold rounded-xl transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
                >
                  <i className="ri-checkbox-circle-line text-base"></i>
                  RSVP NOW
                </button>
                <a
                  href={event.mapLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-600 text-xs font-semibold rounded-xl transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-map-2-line"></i>
                  Get Directions
                </a>
              </div>
            )}
          </div>

          {/* ── Member Advantage Block ── */}
          <div className={`rounded-2xl overflow-hidden border ${isGuest ? 'border-gray-200' : 'border-rose-200'} bg-gradient-to-br ${isGuest ? 'from-gray-50 to-white' : 'from-rose-50 to-white'}`}>
            <div className="px-4 pt-4 pb-3">
              <div className="flex items-center gap-2 mb-1">
                <div className="w-6 h-6 flex items-center justify-center">
                  <i className={`ri-vip-crown-fill ${isGuest ? 'text-gray-400' : 'text-rose-500'} text-base`}></i>
                </div>
                <span className={`text-[11px] font-bold ${isGuest ? 'text-gray-400' : 'text-rose-500'} uppercase tracking-wider`}>Member Advantage</span>
                {isGuest && (
                  <div className="ml-auto flex items-center gap-1 text-[10px] text-gray-400">
                    <i className="ri-lock-line text-xs"></i>
                    Locked
                  </div>
                )}
              </div>
              <p className="text-sm font-bold text-gray-800 mb-3">{isGuest ? 'You\'re missing member perks' : 'Not a member yet?'}</p>

              <ul className="space-y-2 mb-4">
                <li className={`flex items-start gap-2 text-xs ${isGuest ? 'text-gray-400' : 'text-gray-700'}`}>
                  <div className="w-4 h-4 flex items-center justify-center shrink-0 mt-0.5">
                    <i className={`ri-time-fill ${isGuest ? 'text-gray-300' : 'text-rose-400'} text-xs`}></i>
                  </div>
                  Get <strong className="mx-1">early access</strong> before events sell out
                </li>
                <li className={`flex items-start gap-2 text-xs ${isGuest ? 'text-gray-400' : 'text-gray-700'}`}>
                  <div className="w-4 h-4 flex items-center justify-center shrink-0 mt-0.5">
                    <i className={`ri-skip-forward-fill ${isGuest ? 'text-gray-300' : 'text-rose-400'} text-xs`}></i>
                  </div>
                  Skip waitlists — your spot is always secured
                </li>
                <li className={`flex items-start gap-2 text-xs ${isGuest ? 'text-gray-400' : 'text-gray-700'}`}>
                  <div className="w-4 h-4 flex items-center justify-center shrink-0 mt-0.5">
                    <i className={`ri-price-tag-3-fill ${isGuest ? 'text-gray-300' : 'text-rose-400'} text-xs`}></i>
                  </div>
                  Unlock <strong className="mx-1">member-only pricing</strong> on every event
                </li>
              </ul>

              {isGuest ? (
                <button
                  onClick={onUnlock}
                  className="flex items-center justify-center gap-2 w-full py-2.5 bg-gray-800 hover:bg-gray-900 text-white text-xs font-bold rounded-xl transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-lock-unlock-line text-sm"></i>
                  Create Free Account — Unlock Perks
                </button>
              ) : (
                <Link
                  to="/membership"
                  onClick={onClose}
                  className="flex items-center justify-center gap-2 w-full py-2.5 bg-rose-500 hover:bg-rose-600 text-white text-xs font-bold rounded-xl transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-vip-crown-line text-sm"></i>
                  Upgrade to VIP
                </Link>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
